/**
 * The reopened-for-revision banner: a headline, and everything else behind one
 * control.
 *
 * A late foreman PDF saves into an already-sent week, the server reopens that
 * week, and the PM comes back to a number that has moved. The first version of
 * this said so generically (useless); the second said everything inline, which
 * put four clauses of prose into a table row and read as a wall (Brandon,
 * 2026-08-19: "it would make it feel a lot cleaner and actually be more
 * effective if it just said Reopened for revision and then there be a Full
 * Details button next to it"). So the row states the fact, and the detail —
 * when the GC last saw this week, what he was told it cost, which reports
 * landed since, and what happens next — lives in a popover.
 *
 * This module owns the popover's WORDS. `RevisionDetailsTip` owns its shape.
 * Splitting them is what lets the copy be pinned without a render harness.
 *
 * The GC's name arrives as `gcLabel` and is never typed here: the caller reads
 * it from project settings (`gcName`). A literal in this file would be a
 * hardcoded GC name in standing chrome, which the naming rule forbids outright.
 */
import type { RollupRevisionContext, RollupRevisionLatePdf } from '@coffman-tm/shared';
import { formatDateMd, formatMoneyCents } from './format';

/** The headline. One string, both surfaces, so they cannot drift apart. */
export const REVISION_BANNER_LABEL = 'Reopened for revision';

/** The control that opens the detail. Named by Brandon; sentence case, like
 *  every other action label on the week row ("View details", "Transfer Week"). */
export const REVISION_DETAILS_LABEL = 'Full details';

/**
 * How many reports the popover lists before it counts the rest. The inline
 * sentence capped at three because it had to fit a table row; a popover is a
 * block of lines, so the cap moved up to where a list genuinely stops being
 * readable. A week that reopened on more than eight late reports is one the PM
 * opens anyway.
 */
const MAX_LISTED_REPORTS = 8;

/** Which surface is rendering. Both make ONE claim about the highlighting and
 *  name the GC ONCE — they differ only in what they can point at. The week row
 *  promises the GC's copy will be marked up; the detail page can do better,
 *  because the download button that produces that exact copy is inches above
 *  the banner. */
export type RevisionBannerSurface = 'week-row' | 'detail-page';

/** The popover's content, in the order it renders. */
export interface RevisionDetail {
  /** When the GC was last sent this week, and what it cost then against now. */
  sentLine: string;
  /** "2 late reports landed since:" — null when there are none to list. */
  reportsHeading: string | null;
  /** One line per late report, in report-date order; capped, then counted. */
  reportLines: string[];
  /** What happens to the money, and where the marked-up copy is. */
  closing: string;
}

/** "D. Whitlock — 8/5", or just the date when the parse never resolved a
 *  foreman. The date still identifies the report, so the name is dropped, not
 *  faked. */
function describeReport(p: RollupRevisionLatePdf): string {
  const day = formatDateMd(p.report_date);
  return p.foreman_name ? `${p.foreman_name} — ${day}` : day;
}

/** ISO instant → 'M/D'. formatDateMd parses calendar dates, so the instant is
 *  cut to its date part first (the same slice `sentOnLabel` does). */
function sentOnMd(priorSentAt: string | undefined): string | null {
  if (!priorSentAt) return null;
  const day = priorSentAt.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? formatDateMd(day) : null;
}

/**
 * Build the popover's copy. Degrades in two steps rather than one: with no
 * context at all it says what it always could (a late report landed, this is
 * what happens next); with a context but no recorded prior total — every send
 * predating that column — it names the send date and the current figure and
 * drops the comparison, because a $0 baseline would read as the week having
 * gone out empty.
 */
export function revisionDetail(
  ctx: RollupRevisionContext | undefined,
  currentInvoiceTotalCents: number,
  gcLabel: string,
  surface: RevisionBannerSurface = 'week-row',
): RevisionDetail {
  const sent = sentOnMd(ctx?.prior_sent_at);
  const prior = ctx?.prior_invoice_total_cents;
  const now = formatMoneyCents(currentInvoiceTotalCents);

  let sentLine: string;
  if (!sent) {
    sentLine = `A late report landed after this week was sent, so it reopened with the new work folded in. It now comes to ${now}.`;
  } else if (prior === null || prior === undefined) {
    sentLine = `Sent ${sent}. It now comes to ${now}.`;
  } else {
    sentLine = `Sent ${sent} at ${formatMoneyCents(prior)} — now ${now}.`;
  }

  const reports = ctx?.late_pdfs ?? [];
  const rest = reports.length - MAX_LISTED_REPORTS;
  const reportLines = reports.slice(0, MAX_LISTED_REPORTS).map(describeReport);
  if (rest > 0) reportLines.push(`+${rest} more`);

  return {
    sentLine,
    reportsHeading:
      reports.length === 0
        ? null
        : `${reports.length} late report${reports.length === 1 ? '' : 's'} landed since:`,
    reportLines,
    // The highlighting claim is CELL-level as of 2026-08-19 — the resend marks
    // the cells whose values moved, not the whole row. Saying "added rows"
    // here would describe the behavior this copy shipped alongside replacing.
    closing:
      surface === 'detail-page'
        ? `Its total is out of Submitted-to-date until you resend. “Download xlsx” above shows exactly which cells changed, highlighted as ${gcLabel} will see them.`
        : `Its total is out of Submitted-to-date until you resend — ${gcLabel} gets exactly the changed cells highlighted.`,
  };
}
